export type DATASET_ACTION = {
    code: 'process-duplicate';
    stage: 'start' | 'end';
};
import { CoordinatorService } from './coordinator.service';
import { Dataset } from './dataset';
import { XItem } from './x-item';
export class Dataset$ {
    private _datasetMap = new Map<string, Dataset>();
    private _noDuplicateMap = new Map<string, Set<string>>();

    private _previewId2mainIdMap = new Map<string, string>();
    private _mainId2previewIdMap = new Map<string, string>();

    main: Dataset;
    preview: Dataset;

    prefillXItem?: XItem;

    private _isSingleItemMode = false;

    duplKeyNameLs = [
        'id-lowercase',
        'doi-lowercase',
        'url-lowercase',
        'title-startPage',
        'source-volume-year-startPage',
        'authors-title-startPage-year',
    ];

    constructor(private coords: CoordinatorService) {
        this.preview = new Dataset(this.coords, { isPreview: true });
        this._datasetMap.set(this.preview.id, this.preview);
        this.main = new Dataset(this.coords, {});
        this._datasetMap.set(this.main.id, this.main);
    }

    onStart() {
    }

    get isReady() {
        return this.main.isReady && this.preview.isReady;
    }

    public get current(): Dataset {
        if (this.coords.stage$.current?.tag === 'preview') return this.preview;
        else return this.main;
    }

    public get isSingleItemMode() {
        return this._isSingleItemMode;
    }

    public set isSingleItemMode(value) {
        this._isSingleItemMode = value;
        if (value) {
            if (this.current.isPreview)
                this.coords.view$.to('__seen', this.coords.resultList$.activeItem!, true);
        } else {
            this.coords.resultList$.requireDisplayCurrentItem();
        }
    }

    private _pinDuplTag(tag: string) {
        return `off-duplkey-${tag}`;
    }

    async refreshPreviewId2mainIdMap() {
        this._previewId2mainIdMap.clear();
        this._mainId2previewIdMap.clear();

        const mKeyMp = new Map<string, XItem>();
        this.main.mainXitemLs.forEach((mXitm) => {
            mXitm.keysIncludingDuplicates.forEach((k) => mKeyMp.set(k, mXitm));
        });
        this.preview.mainXitemLs.forEach((pXitm) => {
            for (const k of pXitm.keysIncludingDuplicates) {
                const mXitm = mKeyMp.get(k);
                if (mXitm) {
                    this._previewId2mainIdMap.set(pXitm.id, mXitm.id);
                    this._mainId2previewIdMap.set(mXitm.id, pXitm.id);
                }
            }
        });
    }

    fromPreview2MainId(itmId: string) {
        return this._previewId2mainIdMap.get(itmId);
    }

    fromPreview2MainIdLs(ids: string[] | Set<string>): string[] {
        const ls: string[] = [];
        ids.forEach((id) => {
            const mainId = this.fromPreview2MainId(id);
            if (mainId) ls.push(mainId);
        });
        return ls;
    }

    fromMain2PreviewId(itmId: string) {
        return this._mainId2previewIdMap.get(itmId);
    }

    fromMain2PreviewIdLs(ids: string[] | Set<string>): string[] {
        const ls: string[] = [];
        ids.forEach((id) => {
            const mainId = this.fromMain2PreviewId(id);
            if (mainId) ls.push(mainId);
        });
        return ls;
    }

    setActiveDuplkey(tag: string, isOn: boolean) {
        const pTag = this._pinDuplTag(tag);
        isOn ? this.coords.pin$.misc!.off(pTag) : this.coords.pin$.misc!.on(pTag);
        this.main.refreshKeys();
        this.preview.refreshKeys();
    }

    isOnDuplkey(tag: string): boolean {
        return !this.coords.pin$.misc?.has(this._pinDuplTag(tag));
    }

    async update2(mp: Map<string, any[]>) {
        console.log('DATASET update2', mp);
    }

    load3(params: any) {
        console.log('LOAD MAIN', params);
        const xitmLs: XItem[] = [];
        params?.xitemLs?.forEach((x: any) => {
            try {
                const xitem = new XItem(this.coords, x);
                xitem.ownerId = params.ownerId;
                xitmLs.push(xitem);
            } catch (e) {
                this.coords.utils$.consoleError('DATASET$ LOAD3 ERROR', e);
            }
        });

        this.main.clear();

        // Load the no-duplicate map AFTER clearing, so it doesn't get wiped
        if (params?.noDuplicateMap) {
            this.loadNoDuplicateMap(params.noDuplicateMap);
        }

        this.main.importXitemLs(xitmLs, true);
    }

    sortAll(tp: string, isDescending: boolean) {
        if (this.coords.stage$.datasetTag === 'preview') {
            this.preview.sort(tp, isDescending);
            this.main.sort(tp, isDescending);
        } else {
            this.main.sort(tp, isDescending);
            this.preview.sort(tp, isDescending);
        }
    }

    clear() {
        this.main.clear();
        this.preview.clear();
        this._previewId2mainIdMap.clear();
        this._mainId2previewIdMap.clear();
        this._noDuplicateMap.clear();
    }

    get(tag: string): any {
        return this._datasetMap.get(tag);
    }

    addNoDuplicate(mainXItmId: string, duplXItmId: string) {
        let s = this._noDuplicateMap.get(mainXItmId);
        if (!s) {
            s = new Set<string>();
            this._noDuplicateMap.set(mainXItmId, s);
        }
        s.add(duplXItmId);
        this.markForManifestStore();
    }

    noDuplicateSet(mainXItmId: string) {
        return this._noDuplicateMap.get(mainXItmId);
    }

    markForManifestStore() {
        this.main.markForManifestStore();
    }

    serializeNoDuplicateMap() {
        const obj: any = {};
        this._noDuplicateMap.forEach((duplicateIdSet, mainItemId) => {
            obj[mainItemId] = Array.from(duplicateIdSet);
        });
        return obj;
    }

    loadNoDuplicateMap(data: any) {
        this._noDuplicateMap.clear();
        if (!data) return;

        Object.keys(data).forEach((mainItemId) => {
            const duplicateIdArray = data[mainItemId];
            if (Array.isArray(duplicateIdArray)) {
                this._noDuplicateMap.set(mainItemId, new Set(duplicateIdArray));
            }
        });
    }

    initResultList(tp: 'main' | 'preview') {
        const title = this.coords.text(tp === 'preview' ? 'InPreview' : 'Collected');

        let ls = this.coords.resultList$.activeList$.getValue();

        switch (tp) {
            case 'main':
                ls = ls.filter((x) => this.fromPreview2MainId(x.id));
                break;
            case 'preview':
                ls = ls.filter((x) => this.fromMain2PreviewId(x.id));
                break;
        }

        const stg = this.coords.stage$.current!.tag;

        this.coords.resultList$.activateSubset({
            idSet: new Set(ls.map((x) => x.id)),
            title: title,
            prefix: '',
            icon: tp,
            class: `selection-in-${tp}`,
            back: () => this.coords.stage$.set(stg),
            stage: stg,
        });

        // const tDct = this.coords.autotag$.getConstantTags("selection")
        // const st = this.coords.stage$.current!
        // this.coords.autotag$.setConstant(tDct.set, new Set(ls.map(x => x.id)))
        // this.coords.autotag$.setConstant(tDct.isNot, false)
        // this.coords.autotag$.setConstant(tDct.title, {
        //     prefix: prefix,
        //     title: "",
        //     icon: tp,
        //     hasCounter: true,
        //     class: `selection-in-${tp}`,
        //     back: () => this.coords.stage$.set(st)
        // })

        // this.coords.resultList$.generateActiveLs()
    }

    addXitemLsToMain(xitmLs: XItem[], ownerId?: string) {
        const s = this.main.xitemIdSet;
        const ls = xitmLs.filter((xitm) => !s.has(xitm.id));
        ownerId &&
            ls.forEach((x) => {
                if (!x.ownerId) x.ownerId = ownerId;
            });
        this.main.importXitemLs(ls);
        const idLs = xitmLs.map((x) => x.id);
        const v$ = this.coords.view$;
        v$.getAlwaysPresentView('__seen').deleteIds(idLs);
        v$.getAlwaysPresentView('__rejected').deleteIds(idLs);
        this.coords.display$.markForCheck();
    }

    clearFilters() {}
}
